// MessageVideoPlayer.jsx
import React from 'react';
import PropTypes from 'prop-types';

import MediaSourceWrapper from '../container/MediaSourceWrapper.js';
import DecryptionWrapper from '../container/DecryptionWrapper.js';
import ShakaPlayer from './ShakaPlayer.jsx';
import PrestoPlayer from './PrestoPlayer.jsx';
import { PlayerEngine } from '../selector/getCurrentEngine.js';
import { getKeySystem } from '../resource/getDrmInfo.js';
import { drmRulesetTestStatus as DrmTestStatus } from '../resource/drmConstants.js';
// import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import getImageSupport from '../resource/getImageSupport.js';
import { Image } from '../component/LazyImage.jsx';
import { NULL_FUNCTION } from '../resource/defaults.js';

/** @typedef {import('video.js').VideoJsPlayer} VideoJsPlayer */

/**
 * @typedef {Object} Props
 * @property {string} token
 * @property {string} messageId
 * @property {Object} source
 * @property {string} mediaType
 * @property {'preview' | 'sd' | 'hd'} viewScope
 * @property {boolean} canViewMedia
 * @property {boolean} isMuted
 * @property {(arg: { id: string }) => Promise<void>} fetchDrmLicenseAvailability
 *
 * @extends {React.PureComponent<Props>}
 */
class MessageVideoPlayer extends React.PureComponent {
  /** @type {VideoJsPlayer?}*/
  player = null;
  posterFormat = getImageSupport().bestFormat;
  state = { loadedDetailMessageId: null };

  // The asset `aes` flag only arrives with the message detail, and undefined is
  // ambiguous there — "not loaded yet" and "not encrypted" look the same. Latch
  // the message whose detail has loaded so a refetch, which resets `isFetched`
  // while leaving the data in the store, cannot tear the player down.
  static getDerivedStateFromProps(
    { messageId, shouldFetchMessageDetail },
    { loadedDetailMessageId }
  ) {
    if (loadedDetailMessageId === messageId || shouldFetchMessageDetail) {
      return null;
    }
    return { loadedDetailMessageId: messageId };
  }

  componentDidMount() {
    const { messageId, canViewMedia, fetchDrmLicenseAvailability } = this.props;
    if (messageId && canViewMedia === null) {
      fetchDrmLicenseAvailability({
        keySystem: getKeySystem(),
        messageId,
      });
    }

    this.fetchMediaMenifestOfMessage();
    this.fetchMessageDetail();

    this.checkDrmCapabilityBeforePlay();
    this.setPlayerThumbnail();
  }

  componentDidUpdate(prevProps) {
    const {
      meId,
      messageId,
      token,
      drmTestStatus,
      canViewMedia,
      fetchDrmLicenseAvailability,
    } = this.props;
    if (
      messageId !== prevProps.messageId ||
      meId !== prevProps.meId ||
      canViewMedia !== prevProps.canViewMedia
    ) {
      if (messageId && canViewMedia === null) {
        fetchDrmLicenseAvailability({
          keySystem: getKeySystem(),
          messageId,
        });
      }

      this.fetchMediaMenifestOfMessage();
      this.fetchMessageDetail();
    }
    if (canViewMedia !== prevProps.canViewMedia || token !== prevProps.token) {
      this.setPlayerThumbnail();
    }
    if (
      canViewMedia !== prevProps.canViewMedia ||
      token !== prevProps.token ||
      drmTestStatus !== prevProps.drmTestStatus
    ) {
      this.checkDrmCapabilityBeforePlay();
    }
  }

  componentWillUnmount() {
    if (this.player) {
      this.player = undefined;
      delete this.player;
    }
  }

  fetchMediaMenifestOfMessage = () => {
    const { messageId, assetId, fetchMediaMenifestOfMessage } = this.props;
    // Post (has assetId) get mediaType from content-type
    if (!messageId || assetId) {
      return;
    }
    return fetchMediaMenifestOfMessage({ id: messageId });
  };

  fetchMessageDetail = () => {
    const { messageId, shouldFetchMessageDetail, fetchMessageDetail } =
      this.props;
    if (!messageId || !shouldFetchMessageDetail) {
      return;
    }
    return fetchMessageDetail({ id: messageId });
  };

  setPlayerThumbnail = () => {
    // 2023.02.22
    // Temporarily cut out the poster to avoid the video out of shape on Safari,
    // especially for Stories and Feeds.
    //
    // const { thumbnail } = this.props;
    // const srcSet = getPublicSrcSet({
    //   href: thumbnail,
    //   size: 512,
    //   format: this.posterFormat,
    // });
    // this?.player?.poster(srcSet.src);
  };

  checkDrmCapabilityBeforePlay = () => {
    const {
      drmFeatureName,
      drmTestStatus,
      canViewMedia,
      token,
      checkDrmCapabilityBeforePlay,
    } = this.props;
    // check token to prevent check drm for SEO crawlers.
    // player will just play with drm level from remote config.
    if (canViewMedia && token && DrmTestStatus.TESTING !== drmTestStatus) {
      return checkDrmCapabilityBeforePlay({ drmFeatureName });
    }
    return;
  };

  render() {
    const {
      token,
      clientId,
      sessionId,
      viewScope,
      mediaType,
      keySystem,
      robustness,
      isAesEncrypted,
      isAesImage,
      isAssetDataLoaded,
      isDecryptionRequired,
      shouldFetchMessageDetail,
      playbackSource,
      isMuted,
      canViewMedia,
      fetchDrmLicense,
      fetchPlayReadyLicense,
      splashVideo,
      messageId,
      assetId,
      aesImageResourceUrl,
      aesImageBlurredUrl,
      currentEngine,
      ...otherProps
    } = this.props;

    // Wait for the detail to have landed once, not for the current request to
    // be idle — a background refetch flips `isFetched` back to false and tearing
    // the player down there costs a full reload of a short mid-swipe.
    if (!isAssetDataLoaded || this.state.loadedDetailMessageId !== messageId) {
      return null;
    }

    if (isAesImage) {
      return (
        <DecryptionWrapper
          resourceUrl={
            canViewMedia
              ? aesImageResourceUrl
              : isDecryptionRequired
                ? aesImageBlurredUrl
                : undefined
          }
          messageId={messageId}
          assetId={assetId}
          isAesImage
          onError={otherProps.onError}
        >
          <Image
            src={
              canViewMedia
                ? undefined
                : isDecryptionRequired
                  ? undefined
                  : aesImageBlurredUrl
            }
            onLoad={() => {
              // Provide a player-like interface so <Image> can mock real player behavior.
              const listeners = {};
              const mockPlayer = {
                getAssetUri: () =>
                  canViewMedia ? aesImageResourceUrl : aesImageBlurredUrl, // for engagement tracking
                play: () => Promise.resolve(), // for autoplay chaining `.then()`
                paused: () => true, // image is never "playing" so report as paused
                duration: () => 0,
                currentTime: () => 0,
                on: (event, callback) => {
                  listeners[event] = listeners[event] || [];
                  listeners[event].push(callback);
                },
                off: (event, callback) => {
                  if (listeners[event]) {
                    listeners[event] = listeners[event].filter(
                      listenerCallback => listenerCallback !== callback
                    );
                  }
                },
                pause: NULL_FUNCTION,
                poster: NULL_FUNCTION,
                loop: NULL_FUNCTION,
              };
              otherProps.onSetup?.(mockPlayer);
              otherProps.onCanPlay?.();
              listeners['playing']?.forEach(callback => callback());
              otherProps.onPlaying?.({ type: 'playing' });
            }}
            style={{
              objectFit: 'contain',
              ...(otherProps.style || {}),
            }}
          />
        </DecryptionWrapper>
      );
    }

    return (
      <MediaSourceWrapper messageId={messageId} source={playbackSource}>
        {({ mediaSource }) => {
          const playerProps = {
            isMuted,
            canViewMedia,
            token,
            clientId,
            sessionId,
            source: playbackSource,
            splashVideo,
            viewScope,
            mediaType,
            messageId,
            keySystem,
            robustness,
            isAesEncrypted,
            fetchDrmLicense,
            fetchPlayReadyLicense,
            decryptionType: 'video/mp4',
            contentUrl: mediaSource?.contentUrl,
            ...otherProps,
          };

          const isPRESTOplayEngine = currentEngine === PlayerEngine.PRESTOPLAY;

          return (
            <DecryptionWrapper
              resourceUrl={playbackSource?.mp4}
              splashUrl={splashVideo?.dash}
            >
              {isPRESTOplayEngine ? (
                <PrestoPlayer {...playerProps} />
              ) : (
                <ShakaPlayer {...playerProps} />
              )}
            </DecryptionWrapper>
          );
        }}
      </MediaSourceWrapper>
    );
  }
}

MessageVideoPlayer.propTypes = {
  meId: PropTypes.string,
  token: PropTypes.string,
  clientId: PropTypes.string,
  sessionId: PropTypes.string,
  messageId: PropTypes.string,
  aesImageResourceUrl: PropTypes.string,
  aesImageBlurredUrl: PropTypes.string,
  assetId: PropTypes.string,
  drmFeatureName: PropTypes.string,
  playbackSource: PropTypes.object,
  splashVideo: PropTypes.object,
  canViewMedia: PropTypes.bool,
  isMuted: PropTypes.bool,
  isFlix: PropTypes.bool,
  isDecryptionRequired: PropTypes.bool,
  viewScope: PropTypes.string,
  mediaType: PropTypes.string,
  keySystem: PropTypes.string,
  robustness: PropTypes.string,
  isAesEncrypted: PropTypes.bool,
  isAesImage: PropTypes.bool,
  isAssetDataLoaded: PropTypes.bool,
  shouldFetchMessageDetail: PropTypes.bool,
  drmTestStatus: PropTypes.string,
  thumbnail: PropTypes.string,
  widevineServerCertificateBase64: PropTypes.string,
  currentEngine: PropTypes.oneOf([PlayerEngine.PRESTOPLAY, PlayerEngine.SHAKA]),
  fetchDrmLicenseAvailability: PropTypes.func,
  fetchPlayReadyLicense: PropTypes.func,
  fetchDrmLicense: PropTypes.func,
  checkDrmCapabilityBeforePlay: PropTypes.func,
  fetchMediaMenifestOfMessage: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
};

MessageVideoPlayer.defaultProps = {
  meId: '',
  token: '',
  clientId: '',
  sessionId: '',
  messageId: '',
  assetId: '',
  aesImageResourceUrl: '',
  aesImageBlurredUrl: '',
  drmFeatureName: null,
  playbackSource: {},
  splashVideo: {},
  canViewMedia: null,
  isMuted: true,
  isFlix: false,
  viewScope: 'preview',
  mediaType: 'image',
  keySystem: null,
  robustness: null,
  isAesEncrypted: false,
  isAesImage: false,
  isAssetDataLoaded: true,
  isDecryptionRequired: false,
  shouldFetchMessageDetail: false,
  drmTestStatus: null,
  thumbnail: null,
  widevineServerCertificateBase64: '',
  fetchDrmLicenseAvailability: NULL_FUNCTION,
  fetchDrmLicense: NULL_FUNCTION,
  checkDrmCapabilityBeforePlay: NULL_FUNCTION,
  fetchPlayReadyLicense: NULL_FUNCTION,
  fetchMediaMenifestOfMessage: NULL_FUNCTION,
  fetchMessageDetail: NULL_FUNCTION,
  currentEngine: PlayerEngine.SHAKA,
};

export default MessageVideoPlayer;
